/**
 * Build a ticket, see what it pays.
 *
 * "Two men to both make the final flight" is not the product of two singles
 * — there are four seats and the whole field is competing for them, so the
 * legs are correlated and multiplying them would price the ticket wrong in
 * the bettor's favour. The simulation hands back who made the flight in
 * each of a thousand weeks; this counts the ones where EVERY name on the
 * slip made it. Correlation included by construction.
 *
 * Quoted BETTER than fair, deliberately — Scott's ruling that this one leans
 * to the table. On top of taking no margin, the house hands back a bonus that
 * grows with the legs, the way a parlay card does: the long shots are the fun
 * ones, and they are the ones a straight fair price punishes hardest.
 */

/** What the house gives up, by how many names are on the slip. */
const BONUS: Record<number, number> = { 2: 0.15, 3: 0.3 }
const bonusFor = (legs: number): number => (legs >= 4 ? 0.5 : (BONUS[legs] ?? 0))

/**
 * A price that pays the bonus on top of the true chance.
 *
 * Worked in decimal because that is where "pay 15% more" is a multiplication
 * — American odds are two different scales stitched together and the same
 * sum on either side of −100 means different things.
 */
function generousAmerican(p: number, bonus: number): number {
  if (p <= 0.002) return 9900
  if (p >= 0.98) return -9900
  const b = ((1 - p) / p) * (1 + bonus)
  return b >= 1 ? Math.round(b * 100) : -Math.round(100 / b)
}

import { useEffect, useRef, useState } from 'react'
import { weekSets, scoresThrough } from '../engine/model'
import { formatAmerican, formatMoney, payoutCents } from '../engine/odds'
import type { PlayerId, Tournament } from '../engine/types'
import { playerName } from '../select'
import { mergedScores, postOdds, useStore } from '../store'

const SIMS = 1200
const CHUNKS = 8

export function Parlay({
  doc,
  stake,
  mayBook,
  onPosted,
}: {
  doc: Tournament
  /** The board's current stake, so "what it pays" matches everything else. */
  stake: number
  mayBook: boolean
  onPosted: () => Promise<void> | void
}) {
  const scores = useStore(mergedScores)
  const [open, setOpen] = useState(false)
  const [mode, setMode] = useState<'flight' | 'cellar' | 'combo'>('flight')
  /*
   * Two lists, whatever the mode: the men bet INTO the flight and the men
   * bet into the cellar. Flight mode reads the first, bottom-two mode the
   * second, combined reads both — so switching modes never loses a pick.
   */
  const [top, setTop] = useState<PlayerId[]>([])
  const [bottom, setBottom] = useState<PlayerId[]>([])
  const [sets, setSets] = useState<{ flight: PlayerId[]; cellar: PlayerId[] }[] | null>(null)
  const [busy, setBusy] = useState(false)
  const [note, setNote] = useState<string | null>(null)
  const running = useRef(false)

  /*
   * Simulated once, on opening, then every ticket is arithmetic over the
   * stored sets — which is what makes tapping names feel instant. Chunked so
   * the thumb keeps working while it runs.
   */
  useEffect(() => {
    if (!open || sets || running.current) return
    running.current = true
    void (async () => {
      const known = scoresThrough(doc, scores, null)
      const all: { flight: PlayerId[]; cellar: PlayerId[] }[] = []
      for (let c = 0; c < CHUNKS; c++) {
        all.push(...weekSets(doc, known, { sims: SIMS / CHUNKS, seedKey: `parlay#${c}` }))
        setSets([...all])
        await new Promise((r) => setTimeout(r, 0))
      }
      running.current = false
    })()
  }, [open, sets, doc, scores])

  const field = doc.players.filter((p) => p.active)

  // What THIS mode's ticket actually names. Only four flight seats and two
  // cellar seats exist, so the pickers cap there — a fifth name is not a
  // longer ticket, it is an impossible one.
  const wantTop = mode === 'flight' || mode === 'combo' ? top : []
  const wantBottom = mode === 'cellar' || mode === 'combo' ? bottom : []
  const legs = wantTop.length + wantBottom.length
  const hits = sets
    ? sets.filter(
        (w) =>
          wantTop.every((id) => w.flight.includes(id)) &&
          wantBottom.every((id) => w.cellar.includes(id)),
      ).length
    : 0
  const p = sets && sets.length > 0 ? hits / sets.length : 0
  const price = generousAmerican(p, bonusFor(legs))
  const enough =
    mode === 'flight'
      ? wantTop.length >= 2
      : mode === 'cellar'
        ? wantBottom.length >= 2
        : wantTop.length >= 1 && wantBottom.length >= 1
  const ready = sets != null && sets.length >= SIMS && enough

  const names = (ids: PlayerId[]) => ids.map((id) => playerName(doc, id)).join(' and ')
  const wording =
    mode === 'flight'
      ? `${names(wantTop)} to all make Flight 1`
      : mode === 'cellar'
        ? `${names(wantBottom)} to all land in Flight 3`
        : `${names(wantTop)} to make Flight 1, with ${names(wantBottom)} in Flight 3`
  const bookieName = playerName(doc, doc.settings.bookie ?? '') || 'the bookie'

  /*
   * One tap does the obvious thing for the mode. Combined cycles a card
   * through its three meanings — flight, cellar, out — skipping any side
   * that is already full, so the thumb never has to un-pick to re-pick.
   */
  const tap = (id: PlayerId) => {
    const inTop = top.includes(id)
    const inBottom = bottom.includes(id)
    const dropTop = () => setTop((c) => c.filter((x) => x !== id))
    const dropBottom = () => setBottom((c) => c.filter((x) => x !== id))
    if (mode === 'flight') {
      if (inTop) dropTop()
      else if (top.length < 4) {
        // A man cannot ride both ends of one week; adding him here pulls
        // him out of the cellar picked in another mode.
        dropBottom()
        setTop((c) => [...c, id])
      }
      return
    }
    if (mode === 'cellar') {
      if (inBottom) dropBottom()
      else if (bottom.length < 4) {
        dropTop()
        setBottom((c) => [...c, id])
      }
      return
    }
    if (inTop) {
      dropTop()
      if (bottom.length < 2) setBottom((c) => [...c, id])
    } else if (inBottom) {
      dropBottom()
    } else if (top.length < 2) {
      setTop((c) => [...c, id])
    } else if (bottom.length < 2) {
      setBottom((c) => [...c, id])
    }
  }

  const post = async () => {
    setBusy(true)
    setNote(null)
    try {
      await postOdds({
        market: 'Parlay',
        description: wording,
        americanOdds: price,
        subjectId: null,
        propose: !mayBook,
      })
      await onPosted()
      setNote(
        mayBook
          ? 'On the board — anybody can back it now'
          : `Sent to ${bookieName} — pending odds approval, and he just got the buzz`,
      )
      setTop([])
      setBottom([])
    } catch (e) {
      setNote(e instanceof Error ? e.message : 'Could not post it')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="border-b border-rule">
      {/*
        Dressed as a button, not a list row — same clothes as "Play the week
        out" on the Board. The flat strip read as a heading and got walked
        past; a 2px border is the app's word for "press me".
      */}
      <div className="px-4 pt-2.5 pb-2">
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          aria-expanded={open}
          className={`bc-tap w-full border-2 border-ink px-3 py-3 flex items-center justify-between gap-3 ${
            open ? 'bg-ink text-paper' : ''
          }`}
        >
          <span className="bc-label">Build a parlay</span>
          <span className={`text-[13px] ${open ? 'text-paper/70' : 'text-mute'}`}>
            {open ? 'close' : 'Flight 1, Flight 3, or both ›'}
          </span>
        </button>
      </div>

      {open && (
        <div className="px-4 pb-3">
          {/*
            Three tickets, one grid. Flight is the original; the cellar is its
            mirror; combined names both ends of the same week. The chips wear
            the Odds sub-tab clothes so nobody has to learn a new control.
          */}
          <div className="grid grid-cols-3 border border-rule mb-2">
            {(
              [
                ['flight', 'Flight 1'],
                ['cellar', 'Flight 3'],
                ['combo', 'Top & bottom'],
              ] as const
            ).map(([m, label]) => (
              <button
                key={m}
                type="button"
                onClick={() => {
                  setMode(m)
                  if (m === 'combo') {
                    // Combined is two up, two down; a four-name flight slip
                    // walking in would quietly become a six-leg ticket.
                    setTop((c) => c.slice(0, 2))
                    setBottom((c) => c.slice(0, 2))
                  }
                }}
                className={`bc-label py-2.5 bc-tap ${
                  mode === m ? 'bg-ink text-paper' : 'bg-paper text-mute'
                }`}
              >
                {label}
              </button>
            ))}
          </div>

          {/*
            A card per man, not a pill: room for a monogram, a name that
            never truncates, and a tap target a thumb can trust on a tee box.
            A flight pick flips the card to the accent; a cellar pick flips
            it to the signal red, because the wooden spoon should look like
            one — the slip reads at a glance across the cart.
          */}
          <div className="grid grid-cols-3 gap-1.5">
            {field.map((pl) => {
              const onTop = (mode === 'flight' || mode === 'combo') && top.includes(pl.id)
              const onBottom = (mode === 'cellar' || mode === 'combo') && bottom.includes(pl.id)
              const initials = pl.displayName
                .split(/\s+/)
                .map((w) => w[0])
                .join('')
                .slice(0, 2)
                .toUpperCase()
              return (
                <button
                  key={pl.id}
                  type="button"
                  aria-pressed={onTop || onBottom}
                  onClick={() => tap(pl.id)}
                  className={`bc-tap rounded-lg border py-2 flex flex-col items-center gap-1 ${
                    onTop
                      ? 'bg-pop text-paper border-pop'
                      : onBottom
                        ? 'bg-signal text-paper border-signal'
                        : 'border-rule bg-paper'
                  }`}
                >
                  <span
                    aria-hidden
                    className={`w-7 h-7 rounded-full flex items-center justify-center text-[11px] font-semibold tracking-wide ${
                      onTop || onBottom ? 'bg-paper/20' : 'bg-ink/8 text-mute'
                    }`}
                  >
                    {onTop ? '\u2191' : onBottom ? '\u2193' : initials}
                  </span>
                  <span className="bc-name text-[13px] leading-none">{pl.displayName}</span>
                </button>
              )
            })}
          </div>

          <div className="mt-3">
            {sets == null || sets.length < SIMS ? (
              <p className="text-[13px] text-mute">
                Playing the week out{sets ? ` — ${Math.round((sets.length / SIMS) * 100)}%` : '…'}
              </p>
            ) : !enough ? (
              <p className="text-[13px] text-mute">
                {mode === 'flight'
                  ? 'Name at least two for Flight 1.'
                  : mode === 'cellar'
                    ? 'Name at least two you say land in Flight 3.'
                    : 'Name somebody for Flight 1 and somebody for Flight 3 — up to two of each.'}
              </p>
            ) : (
              <>
                <p className="text-sm leading-snug">
                  <b className="bc-name">{wording}</b>
                </p>
                <p className="text-[13px] mt-1">
                  <span className="bc-num text-lg">{formatAmerican(price)}</span>
                  <span className="text-mute">
                    {' '}· hits {(p * 100).toFixed(1)}% of simulated weeks ·{' '}
                    {formatMoney(stake)} returns{' '}
                    {formatMoney(stake + payoutCents(stake, price))}
                  </span>
                </p>
                {p === 0 && (
                  <p className="text-[12px] text-signal mt-1">
                    Never happened in {sets.length.toLocaleString()} weeks. The bookie
                    would be pricing a miracle — pick fewer names.
                  </p>
                )}
                <button
                  type="button"
                  disabled={busy || !ready || p === 0}
                  onClick={() => void post()}
                  className="bc-tap bc-label px-3 mt-2 bg-ink text-paper disabled:opacity-40"
                >
                  {busy
                    ? 'Sending…'
                    : mayBook
                      ? 'Put it on the board'
                      : `Send it to ${bookieName}`}
                </button>
                {note && <p className="text-[12px] text-mute mt-1">{note}</p>}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
